import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import api from "../../utils/api";
import { getImageUrl } from "../../utils/imageUtils";
import { toLocalizedNumber } from "../../utils/localeUtils";
import Pagination from "../../components/Pagination";
import { FaStar, FaRegStar, FaCheck } from "react-icons/fa";
import "./RatingsReviews.css";

const PER_PAGE = 10;

const RatingsReviews = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("all");
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setLoadingReviews(true);
    api
      .get("/reviews/latest", { params: { limit: 50 } })
      .then((res) => {
        if (!cancelled && Array.isArray(res.data)) {
          setReviews(
            res.data.map((r) => ({
              _id: r._id,
              user: {
                name: r.consumer?.name || "Customer",
                image: r.consumer?.profileImage || null,
                verified: !!r.isVerified,
              },
              product: {
                _id: r.product?._id,
                name: r.product?.name || "Product",
                image: r.product?.images?.[0] || null,
              },
              farmer: {
                name: r.farmer?.name || "Farmer",
                _id: r.farmer?._id,
                username: r.farmer?.username,
              },
              rating: r.rating,
              comment: r.comment || "",
              date: r.createdAt ? new Date(r.createdAt) : null,
              verifiedPurchase: !!r.isVerified,
            })),
          );
        }
      })
      .catch(() => {
        if (!cancelled) setReviews([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingReviews(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredReviews =
    filter === "all"
      ? reviews
      : reviews.filter((r) => r.rating === parseInt(filter, 10));

  const totalPages = Math.ceil(filteredReviews.length / PER_PAGE);
  const pageReviews = filteredReviews.slice(
    (page - 1) * PER_PAGE,
    page * PER_PAGE,
  );

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const averageRating =
    reviews.length > 0
      ? (
          reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
        ).toFixed(1)
      : "0";
  const ratingDistribution = {
    5: reviews.filter((r) => r.rating === 5).length,
    4: reviews.filter((r) => r.rating === 4).length,
    3: reviews.filter((r) => r.rating === 3).length,
    2: reviews.filter((r) => r.rating === 2).length,
    1: reviews.filter((r) => r.rating === 1).length,
  };
  const renderStars = (rating) => (
    <span className="ratings-reviews-stars-row" aria-hidden>
      {Array.from({ length: 5 }, (_, i) =>
        i < rating ? (
          <FaStar
            key={i}
            className="ratings-reviews-star ratings-reviews-star--filled"
          />
        ) : (
          <FaRegStar
            key={i}
            className="ratings-reviews-star ratings-reviews-star--empty"
          />
        ),
      )}
    </span>
  );

  return (
    <div className="ratings-reviews-page">
      <Header />

      <div className="ratings-reviews-container">
        <div className="ratings-reviews-header">
          <h1 className="ratings-reviews-title">{t("ratingsReviews.title")}</h1>
          <p className="ratings-reviews-subtitle">
            {t("ratingsReviews.subtitle")}
          </p>
        </div>

        {/* Overall rating summary */}
        <div className="ratings-reviews-rating-summary-section">
          {loadingReviews ? (
            <div className="ratings-reviews-summary-loading">
              <div className="ratings-reviews-spinner" />
              <p>{t("ratingsReviews.loading")}</p>
            </div>
          ) : (
            <div className="ratings-reviews-rating-overview">
              <div className="ratings-reviews-average-rating">
                <div className="ratings-reviews-rating-number-large">
                  {toLocalizedNumber(averageRating)}
                </div>
                <div className="ratings-reviews-rating-stars-large">
                  {renderStars(Math.round(Number(averageRating)))}
                </div>
                <div className="ratings-reviews-rating-count">
                  {t("ratingsReviews.basedOn", {
                    count: toLocalizedNumber(reviews.length),
                  })}
                </div>
              </div>
              <div className="ratings-reviews-rating-breakdown">
                {[5, 4, 3, 2, 1].map((rating) => (
                  <div key={rating} className="ratings-reviews-rating-bar-item">
                    <button
                      className={`ratings-reviews-rating-filter-btn ${filter === rating.toString() ? "active" : ""}`}
                      onClick={() =>
                        setFilter(
                          filter === rating.toString()
                            ? "all"
                            : rating.toString(),
                        )
                      }
                    >
                      <span className="ratings-reviews-filter-star-wrap">
                        {toLocalizedNumber(rating)}{" "}
                        <FaStar
                          className="ratings-reviews-filter-star-icon"
                          aria-hidden
                        />
                      </span>
                    </button>
                    <div className="ratings-reviews-rating-bar">
                      <div
                        className="ratings-reviews-rating-bar-fill"
                        style={{
                          width: `${reviews.length > 0 ? (ratingDistribution[rating] / reviews.length) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <span className="ratings-reviews-rating-count-small">
                      {toLocalizedNumber(ratingDistribution[rating])}
                    </span>
                  </div>
                ))}
                <button
                  type="button"
                  className={`ratings-reviews-filter-all-btn ${filter === "all" ? "active" : ""}`}
                  onClick={() => setFilter("all")}
                >
                  {t("ratingsReviews.showAll", {
                    count: toLocalizedNumber(reviews.length),
                  })}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Reviews list */}
        <div className="ratings-reviews-reviews-list-section">
          {loadingReviews ? (
            <div className="ratings-reviews-list-loading">
              <div className="ratings-reviews-spinner" />
              <p>{t("ratingsReviews.loading")}</p>
            </div>
          ) : (
            <div className="ratings-reviews-reviews-list">
              {pageReviews.map((review) => (
                <div key={review._id} className="ratings-reviews-review-card">
                  <div className="ratings-reviews-review-header">
                    <div className="ratings-reviews-review-user-info">
                      <img
                        src={
                          review.user?.image
                            ? getImageUrl(review.user.image)
                            : "/images/deafultpic.png"
                        }
                        alt={review.user?.name || "User"}
                        className="ratings-reviews-review-user-avatar"
                      />
                      <div className="ratings-reviews-review-user-details">
                        <div className="ratings-reviews-review-user-name">
                          {review.user?.name || "Customer"}
                          {review.user?.verified && (
                            <span
                              className="ratings-reviews-verified-badge-small"
                              title="Verified"
                            >
                              <FaCheck aria-hidden />
                            </span>
                          )}
                        </div>
                        <div className="ratings-reviews-review-user-meta">
                          {review.verifiedPurchase && (
                            <span className="ratings-reviews-verified-purchase">
                              <FaCheck
                                aria-hidden
                                className="ratings-reviews-verified-purchase-icon"
                              />{" "}
                              {t("ratingsReviews.verifiedPurchase")}
                            </span>
                          )}
                          <span className="ratings-reviews-review-date">
                            {review.date
                              ? new Date(review.date).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "long",
                                    day: "numeric",
                                    year: "numeric",
                                  },
                                )
                              : "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="ratings-reviews-review-rating">
                      {renderStars(review.rating)}
                    </div>
                  </div>
                  <div className="ratings-reviews-review-product-info">
                    <Link
                      to={`/products/${review.product?._id}`}
                      className="ratings-reviews-product-link"
                    >
                      {review.product?.image && (
                        <img
                          src={getImageUrl(review.product.image)}
                          alt={review.product?.name || ""}
                          className="ratings-reviews-review-product-image"
                        />
                      )}
                      {review.product?.name || "Product"}
                    </Link>
                    <span className="ratings-reviews-product-farmer">
                      {t("ratingsReviews.by")}{" "}
                      <Link
                        to={`/users/${review.farmer?.username || review.farmer?._id || ""}`}
                      >
                        {review.farmer?.name || "Farmer"}
                      </Link>
                    </span>
                  </div>
                  {review.comment ? (
                    <div className="ratings-reviews-review-comment">
                      {review.comment}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
          {!loadingReviews && filteredReviews.length === 0 && (
            <div className="ratings-reviews-no-reviews">
              <p>
                {reviews.length === 0
                  ? t("ratingsReviews.noReviewsYet")
                  : t("ratingsReviews.noReviewsForRating")}
              </p>
            </div>
          )}
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RatingsReviews;
